import { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { Settings, Maximize, Minimize, Volume2, VolumeX, Play, Pause } from 'lucide-react';

interface VideoQuality {
  label: string;
  url: string;
  height: number;
}

interface AdvancedVideoPlayerProps {
  videoUrl: string;
  qualities?: VideoQuality[];
  onStart?: () => void;
  onProgress?: (state: { playedSeconds: number; played: number }) => void;
  onEnded?: () => void;
  startPosition?: number;
}

export function AdvancedVideoPlayer({
  videoUrl,
  qualities = [],
  onStart,
  onProgress,
  onEnded,
  startPosition = 0,
}: AdvancedVideoPlayerProps) {
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentQuality, setCurrentQuality] = useState<VideoQuality | null>(null);
  const [autoQuality, setAutoQuality] = useState(true);
  const [progress, setProgress] = useState({ played: 0, playedSeconds: 0, loaded: 0 });
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<number | null>(null);

  // Inicializar qualidade
  useEffect(() => {
    if (qualities.length > 0 && !currentQuality) {
      // Seleciona qualidade média por padrão (720p se disponível)
      const defaultQuality = qualities.find(q => q.height === 720) || qualities[Math.floor(qualities.length / 2)];
      setCurrentQuality(defaultQuality);
    }
  }, [qualities, currentQuality]);

  // Carregar posição inicial
  useEffect(() => {
    if (playerRef.current && startPosition > 0) {
      playerRef.current.seekTo(startPosition, 'seconds');
    }
  }, [startPosition]);

  // Detectar mudança de velocidade de internet e ajustar qualidade automaticamente
  useEffect(() => {
    if (!autoQuality || qualities.length === 0) return;

    const checkConnectionSpeed = () => {
      // Usa Network Information API se disponível
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      
      if (connection) {
        const effectiveType = connection.effectiveType; // '4g', '3g', '2g', 'slow-2g'
        
        let recommendedHeight = 720;
        
        switch (effectiveType) {
          case '4g':
            recommendedHeight = 1080;
            break;
          case '3g':
            recommendedHeight = 720;
            break;
          case '2g':
          case 'slow-2g':
            recommendedHeight = 480;
            break;
        }
        
        const recommended = qualities.find(q => q.height === recommendedHeight) || 
                          qualities.reduce((prev, curr) => 
                            Math.abs(curr.height - recommendedHeight) < Math.abs(prev.height - recommendedHeight) 
                              ? curr 
                              : prev
                          );
        
        if (recommended && recommended !== currentQuality) {
          setCurrentQuality(recommended);
        }
      }
    };

    checkConnectionSpeed();
    
    // Verificar a cada 30 segundos
    const interval = setInterval(checkConnectionSpeed, 30000);
    
    return () => clearInterval(interval);
  }, [autoQuality, qualities, currentQuality]);

  const handleProgress = (state: any) => {
    setProgress(state);
    onProgress?.(state);
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleQualityChange = (quality: VideoQuality) => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    setCurrentQuality(quality);
    setAutoQuality(false);
    setShowSettings(false);
    
    // Retomar na mesma posição após trocar qualidade
    setTimeout(() => {
      if (playerRef.current) {
        playerRef.current.seekTo(currentTime, 'seconds');
      }
    }, 100);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (playerRef.current) {
      playerRef.current.seekTo(newTime, 'seconds');
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = window.setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const videoSource = currentQuality?.url || videoUrl;

  return (
    <div 
      ref={containerRef}
      className="relative bg-black aspect-video group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <ReactPlayer
        ref={playerRef}
        url={videoSource}
        width="100%"
        height="100%"
        playing={isPlaying}
        volume={volume}
        muted={isMuted}
        onStart={onStart}
        onProgress={handleProgress}
        onEnded={onEnded}
        onDuration={handleDuration}
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
            },
            hlsOptions: {
              enableWorker: true,
              lowLatencyMode: false,
              backBufferLength: 90,
            },
          },
        }}
      />

      {/* Controles customizados */}
      <div 
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Barra de progresso */}
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={progress.playedSeconds}
          onChange={handleSeek}
          className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer mb-3 range-slider"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(progress.played * 100)}%, #4b5563 ${(progress.played * 100)}%, #4b5563 100%)`
          }}
        />

        <div className="flex items-center justify-between text-white">
          {/* Controles da esquerda */}
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlay}
              className="hover:text-blue-400 transition-colors"
              aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleMute}
                className="hover:text-blue-400 transition-colors"
                aria-label={isMuted ? 'Ativar som' : 'Silenciar'}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={isMuted ? 0 : volume}
                onChange={(e) => {
                  const newVolume = parseFloat(e.target.value);
                  setVolume(newVolume);
                  setIsMuted(newVolume === 0);
                }}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <span className="text-sm">
              {formatTime(progress.playedSeconds)} / {formatTime(duration)}
            </span>
          </div>

          {/* Controles da direita */}
          <div className="flex items-center gap-4">
            {/* Seletor de qualidade */}
            {qualities.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="hover:text-blue-400 transition-colors flex items-center gap-1"
                  aria-label="Configurações de qualidade"
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-xs">
                    {autoQuality ? 'Auto' : `${currentQuality?.label || 'Auto'}`}
                  </span>
                </button>

                {showSettings && (
                  <div className="absolute bottom-full right-0 mb-2 bg-gray-900 rounded-lg shadow-lg p-2 min-w-[150px]">
                    <div className="text-xs text-gray-400 px-2 py-1">Qualidade</div>
                    
                    <button
                      onClick={() => {
                        setAutoQuality(true);
                        setShowSettings(false);
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-800 ${
                        autoQuality ? 'text-blue-400' : 'text-white'
                      }`}
                    >
                      Auto {autoQuality && `(${currentQuality?.label})`}
                    </button>

                    {qualities.map((quality) => (
                      <button
                        key={quality.label}
                        onClick={() => handleQualityChange(quality)}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-800 ${
                          currentQuality === quality && !autoQuality ? 'text-blue-400' : 'text-white'
                        }`}
                      >
                        {quality.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={toggleFullscreen}
              className="hover:text-blue-400 transition-colors"
              aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            >
              {isFullscreen ? (
                <Minimize className="w-5 h-5" />
              ) : (
                <Maximize className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* CSS para o slider */}
      <style>{`
        .range-slider::-webkit-slider-thumb {
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }
        .range-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
}
